import React, { useState, useMemo } from "react";
import { Form, Select, Tooltip, Checkbox, Space, Row, Col } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { Booking } from "../../../types/booking";

const { Option } = Select;

interface BookingSelectorProps {
  bookings: Booking[];
  onBookingChange: (value: number | null) => void;
  isEditMode: boolean;
  getBookingDisplayName: (booking: Booking) => string;
}

const BookingSelector: React.FC<BookingSelectorProps> = ({
  bookings,
  onBookingChange,
  isEditMode,
  getBookingDisplayName,
}) => {
  const form = Form.useFormInstance();
  const selectedBookingId = Form.useWatch("booking_id", form) as number | undefined;
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [hideInvoicedBookings, setHideInvoicedBookings] =
    useState<boolean>(false);

  // Get unique booking statuses
  const bookingStatuses = useMemo(() => {
    const statuses = [
      ...new Set(bookings.map((booking) => booking.status)),
    ].filter(Boolean);
    return statuses.sort();
  }, [bookings]);

  const applyFilters = (status: string, hideInvoiced: boolean) => {
    let filtered = bookings;
    if (status !== "all") {
      filtered = filtered.filter((booking) => booking.status === status);
    }
    if (hideInvoiced) {
      filtered = filtered.filter(
        (booking) =>
          !booking.invoices ||
          !Array.isArray(booking.invoices) ||
          booking.invoices.length === 0
      );
    }
    return filtered;
  };

  // Filter bookings based on status and invoice status
  const filteredBookings = useMemo(
    () => applyFilters(statusFilter, hideInvoicedBookings),
    [bookings, statusFilter, hideInvoicedBookings]
  );

  // Keep the current selection visible even if the active filter would hide it,
  // so the Select value stays valid and a new choice can be made.
  const bookingsForSelect = useMemo(() => {
    if (!selectedBookingId) return filteredBookings;
    if (filteredBookings.some((b) => b.id === selectedBookingId)) {
      return filteredBookings;
    }
    const selected = bookings.find((b) => b.id === selectedBookingId);
    return selected ? [selected, ...filteredBookings] : filteredBookings;
  }, [filteredBookings, selectedBookingId, bookings]);

  const clearBookingIfFilteredOut = (
    status: string,
    hideInvoiced: boolean
  ) => {
    if (!selectedBookingId) return;
    const next = applyFilters(status, hideInvoiced);
    if (!next.some((b) => b.id === selectedBookingId)) {
      form.setFieldsValue({ booking_id: undefined });
      onBookingChange(null);
    }
  };

  const handleStatusFilterChange = (status: string) => {
    setStatusFilter(status);
    clearBookingIfFilteredOut(status, hideInvoicedBookings);
  };

  const handleHideInvoicedChange = (checked: boolean) => {
    setHideInvoicedBookings(checked);
    clearBookingIfFilteredOut(statusFilter, checked);
  };

  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label="Filter by Status">
            <Select
              value={statusFilter}
              onChange={handleStatusFilterChange}
              style={{ width: "100%" }}
              placeholder="All statuses"
            >
              <Option value="all">
                All Statuses ({bookings.length})
              </Option>
              {bookingStatuses.map((status) => (
                <Option key={status} value={status}>
                  {status} ({bookings.filter((b) => b.status === status).length}
                  )
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label=" " style={{ marginBottom: 0 }}>
            <Checkbox
              checked={hideInvoicedBookings}
              onChange={(e) => handleHideInvoicedChange(e.target.checked)}
            >
              Hide bookings with existing invoices
            </Checkbox>
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        name="booking_id"
        label={
          <span>
            Booking{" "}
            <Tooltip title="Select a booking to generate invoice lines automatically based on pricing templates that match the booking's transport type, origin, destination, weight, and volume. Checkboxes indicate bookings that already have invoices.">
              <QuestionCircleOutlined style={{ cursor: "pointer" }} />
            </Tooltip>
          </span>
        }
        rules={[{ required: true, message: "Please select a booking" }]}
      >
        <Select
          placeholder="Select a booking"
          onChange={onBookingChange}
          optionFilterProp="children"
          showSearch
          allowClear
          filterOption={(input, option) => {
            const bookingId = String(option?.value ?? "");
            const search = input.toLowerCase();
            if (bookingId.includes(search)) return true;
            const booking = bookingsForSelect.find((b) => b.id === option?.value);
            if (!booking) return false;
            return getBookingDisplayName(booking).toLowerCase().includes(search);
          }}
          disabled={isEditMode}
          notFoundContent={
            bookings.length === 0
              ? "Loading bookings..."
              : "No bookings match this filter"
          }
        >
          {bookingsForSelect.map((booking) => {
            const hasInvoices =
              booking.invoices &&
              Array.isArray(booking.invoices) &&
              booking.invoices.length > 0;
            return (
              <Option key={booking.id} value={booking.id}>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <Checkbox
                    checked={hasInvoices}
                    disabled
                    style={{ pointerEvents: "none" }}
                  />
                  <span>{getBookingDisplayName(booking)}</span>
                  {hasInvoices && (
                    <span
                      style={{
                        fontSize: "11px",
                        color: "#666",
                        marginLeft: "auto",
                      }}
                    >
                      (Has invoice
                      {booking.invoices && booking.invoices.length > 1
                        ? "s"
                        : ""}
                      )
                    </span>
                  )}
                </div>
              </Option>
            );
          })}
        </Select>
      </Form.Item>
    </Space>
  );
};

export default BookingSelector;
